import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnInit,
  signal,
  inject,
} from '@angular/core';
import { Store } from '@ngxs/store';
import { ActivitiesGateway } from '@wilson/api/gateway';
import { AuthState } from '@wilson/auth/core';
import {
  Activity,
  ActivityCategoriesIdsMap,
  ActivityWithCategoryAndLocationsAndReportedDetails,
  GeoLocation,
  OrganizationalUnit,
} from '@wilson/interfaces';
import { LocationsStoreService } from '@wilson/locations';
import {
  DropZoneEventThrottlerDirective,
  GanttDateToPositionPxPipe,
  GanttEndingPixelPipe,
  GanttStartingPixelPipe,
} from '@wilson/shift-timeline/assignments-gantt-chart';
import {
  GanttShiftEndTimePipe,
  GanttShiftStartTimePipe,
  ShiftTimelinePipesModule,
} from '@wilson/shift-timeline/pipes';
import {
  CreateNewViaDragAndDropService,
  DragAndDropModeService,
  generateDummyActivity,
} from '@wilson/shift-timeline/services';
import {
  ShiftTimelineDataState,
  TimelineRemoveActivityFromShiftV2,
  TimelineUpsertActivityV2,
} from '@wilson/shift-timeline/state';
import { firstValueFrom } from 'rxjs';
import { ActivityDropZoneHelperService } from '../../components/activity-drop-zone/activity-drop-zone-helper.service';
import { TimelineUserRowDropZoneCommonOperationsService } from '../../components/timeline-user-row-shifts/timeline-user-row-common-drop-zone-operations.service';
import { DragAndDropHelperService } from '../../services/drag-and-drop-helper.service';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faBan } from '@fortawesome/pro-solid-svg-icons';

@Component({
  selector: 'wilson-add-empty-activity-to-shift-drop-zone',
  standalone: true,
  imports: [ShiftTimelinePipesModule, FontAwesomeModule],
  template: `
    @if (!isDropAllowed && isHovered) {
      <div
        [style.left.px]="leftPosition()"
        [style.right.px]="rightPosition()"
        class="activityDropZone not-allowed"
      >
        <fa-icon [icon]="faBan" class="icon"></fa-icon>
      </div>
    } @else {
      <div
        [style.left.px]="leftPosition()"
        [style.right.px]="rightPosition()"
        class="activityDropZone"
      ></div>
    }
  `,
  styleUrls: ['./add-empty-activity-to-shift-drop-zone.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    GanttDateToPositionPxPipe,
    GanttShiftStartTimePipe,
    GanttShiftEndTimePipe,
    GanttStartingPixelPipe,
    GanttEndingPixelPipe,
    TimelineUserRowDropZoneCommonOperationsService,
  ],
})
export class AddEmptyActivityToShiftDropZoneComponent
  extends DropZoneEventThrottlerDirective
  implements OnInit
{
  private readonly store = inject(Store);
  private readonly dragAndDropModeService = inject(DragAndDropModeService);
  private locationsStoreService = inject(LocationsStoreService);
  private readonly createNewViaDragAndDropService = inject(
    CreateNewViaDragAndDropService,
  );
  private readonly activitiesService = inject(ActivitiesGateway);
  private readonly ganttShiftStartTimePipe = inject(GanttShiftStartTimePipe);
  private readonly ganttShiftEndTimePipe = inject(GanttShiftEndTimePipe);
  private readonly ganttActivityStartingPixelPipe = inject(
    GanttStartingPixelPipe,
  );
  private readonly ganttActivityEndingPixelPipe = inject(GanttEndingPixelPipe);
  private dragAndDropHelperService = inject(DragAndDropHelperService);
  private readonly activityDropZoneHelperService = inject(
    ActivityDropZoneHelperService,
  );
  private readonly timelineUserRowDropZoneCommonOperationsService = inject(
    TimelineUserRowDropZoneCommonOperationsService,
  );

  @Input() userId!: string | undefined;
  @Input() shiftId!: string;
  @Input() isDropAllowed!: boolean | null;

  public isHovered = false;
  private organizationalUnit!: OrganizationalUnit | undefined;
  location!: GeoLocation | undefined;
  mockActivity!: ActivityWithCategoryAndLocationsAndReportedDetails;
  leftPosition = signal<number>(0);
  rightPosition = signal<number>(0);
  activities!: ActivityWithCategoryAndLocationsAndReportedDetails[];
  readonly faBan = faBan;
  private readonly ADJUSTED_LEFT_PX = 48.9;
  private readonly ACTIVITY_DURATION = 3600000;

  async ngOnInit(): Promise<void> {
    this.activities = this.store.selectSnapshot(
      ShiftTimelineDataState.shiftActivitiesArray(this.shiftId),
    );

    this.organizationalUnit = this.store.selectSnapshot(
      AuthState.organizationalUnit,
    );
    if (this.organizationalUnit?.locationId) {
      firstValueFrom(
        this.locationsStoreService.getLocationFromCache(
          this.organizationalUnit.locationId as string,
        ),
      ).then((location) => {
        this.location = location;
      });
    }
    await this.calculateDropZonePixelPosition();
  }

  protected async onDragOverHandler(offsetX: number) {
    this.isHovered = true;

    if (this.isDropAllowed) {
      this.dragAndDropModeService.setDraggingItemId(
        CreateNewViaDragAndDropService.MOCK_ACTIVITY_ID,
      );
      const activityLeftPixelPosition =
        offsetX + this.leftPosition() - this.ADJUSTED_LEFT_PX;

      const activityPositionData =
        this.dragAndDropHelperService.dragItemDatesAndDisplayRange(
          activityLeftPixelPosition,
          this.ACTIVITY_DURATION,
        );
      const shiftToUpdateId = this.shiftId;

      const isActivityWithinShiftBounds = this.isWithinShiftBounds(
        activityPositionData.itemEndDate.toISOString(),
        activityPositionData.itemStartDate.toISOString(),
      );

      if (
        this.organizationalUnit &&
        this.userId &&
        shiftToUpdateId &&
        this.location &&
        isActivityWithinShiftBounds
      ) {
        this.mockActivity = generateDummyActivity({
          orgUnit: this.organizationalUnit,
          location: this.location,
          activityEndDate: activityPositionData.itemEndDate.toISOString(),
          activityStartDate: activityPositionData.itemStartDate.toISOString(),
          activityCategoryId: ActivityCategoriesIdsMap.TrainDrive,
        });
        this.activityDropZoneHelperService.setActivity(this.mockActivity);

        this.store.dispatch(
          new TimelineUpsertActivityV2([this.mockActivity], shiftToUpdateId),
        );
        this.dragAndDropModeService.updateDemarcatorSettings(
          activityPositionData.currentDemarcatorRange,
        );
      }
    }
  }

  private isWithinShiftBounds(
    activityEndDate: string,
    activityStartDate: string,
  ) {
    const activities = this.activities;
    let shiftStartDateTime = activities[0].startDatetime;
    let shiftEndDateTime = activities[0].endDatetime;

    activities.forEach((activity) => {
      if (activity.startDatetime < shiftStartDateTime)
        shiftStartDateTime = activity.startDatetime;
      if (activity.endDatetime > shiftEndDateTime)
        shiftEndDateTime = activity.endDatetime;
    });

    return (
      shiftStartDateTime < activityStartDate &&
      shiftEndDateTime > activityEndDate
    );
  }

  protected async onDragLeaveHandler() {
    this.isHovered = false;
    const shiftToUpdateId = this.shiftId;
    if (this.userId && shiftToUpdateId) {
      this.store.dispatch([
        new TimelineRemoveActivityFromShiftV2(
          CreateNewViaDragAndDropService.MOCK_ACTIVITY_ID,
          shiftToUpdateId,
        ),
      ]);
      this.resetMoveMode();
    }
  }

  protected async onDropHandler() {
    this.isHovered = false;
    const shiftToUpdateId = this.shiftId;
    const dummyActivity = await firstValueFrom(
      this.activityDropZoneHelperService.activity$,
    );

    if (dummyActivity && this.userId && shiftToUpdateId) {
      // eslint-disable-next-line
      const { id, ...mockActivityWithoutId } = dummyActivity;

      this.timelineUserRowDropZoneCommonOperationsService.prepareForNewQuickActivityShiftCreation(
        shiftToUpdateId,
      );
      this.activitiesService
        .createActivities([
          {
            ...mockActivityWithoutId,
            shiftId: shiftToUpdateId,
          },
        ])
        .subscribe({
          next: (materializedActivity: Activity[]) => {
            this.store.dispatch([
              new TimelineRemoveActivityFromShiftV2(
                CreateNewViaDragAndDropService.MOCK_ACTIVITY_ID,
                shiftToUpdateId,
              ),
              new TimelineUpsertActivityV2(
                materializedActivity as ActivityWithCategoryAndLocationsAndReportedDetails[],
                shiftToUpdateId,
              ),
            ]);

            this.timelineUserRowDropZoneCommonOperationsService.setDragCardVisibleAndRefreshShiftValidations(
              this.userId,
            );
            this.resetMoveMode();
          },
          error: () => {
            this.store.dispatch(
              new TimelineRemoveActivityFromShiftV2(
                CreateNewViaDragAndDropService.MOCK_ACTIVITY_ID,
                shiftToUpdateId,
              ),
            );

            this.timelineUserRowDropZoneCommonOperationsService.handleQuickShiftErrorCleanup(
              shiftToUpdateId,
            );

            this.resetMoveMode();
          },
          complete: () => {
            this.createNewViaDragAndDropService.removeUpdatingId(
              shiftToUpdateId,
            );
          },
        });
    }
  }

  private resetMoveMode() {
    this.dragAndDropModeService.setDraggingItemId(null);
    this.dragAndDropModeService.setDisplayMovingDemarcatorLine(false);
  }

  private async calculateDropZonePixelPosition() {
    const accurateStartDateTimeDetails = this.ganttShiftStartTimePipe.transform(
      this.activities,
    );

    const accurateEndDateTimeDetails = this.ganttShiftEndTimePipe.transform(
      this.activities,
    );

    this.leftPosition.set(
      await firstValueFrom(
        this.ganttActivityStartingPixelPipe.transform(
          accurateStartDateTimeDetails.date,
        ),
      ),
    );

    this.rightPosition.set(
      await firstValueFrom(
        this.ganttActivityEndingPixelPipe.transform([
          accurateStartDateTimeDetails.date,
          accurateEndDateTimeDetails.date,
        ]),
      ),
    );
  }
}
